import { isEqual } from 'lodash';
import {
  navigateToALPPage,
  navigateTo
} from '@cat-ecom/common/utils/browserUtils';
import { selectEquipment } from 'services/equipment/actions';
import { getSeoUrl } from 'utils/links';
import {
  ALP_URL,
  MAINTENANCE_REPAIRS_PATH,
  PARTS_DIAGRAM_PATH,
  HEADLESS_ALP_PAGE,
  MANUALS_AND_RESOURCES_PATH,
  SHOP_PARTS_PATH,
  SERVICE_RECOMMENDATION_LIST_PAGE
} from 'constants/links';
import { capitalizeText } from '@cat-ecom/common/utils/commonUtils';
import {
  Equipment,
  InstanceData,
  ServiceInsightDetailProps
} from 'services/equipment/types';
import {
  SERVICE_ACTIVE,
  SERVICE_DISMISSED
} from 'hooks/use-service-insights/constant/constant';

export const redirectFromEquipment = async (
  url: string,
  isSelectedEquipment: boolean,
  equipmentDetails: {
    id: string;
    serialNumber: string;
  }
) => {
  const response =
    !isSelectedEquipment &&
    (await selectEquipment(
      equipmentDetails?.id,
      equipmentDetails?.serialNumber,
      false
    ));

  if (isSelectedEquipment || response.ok) {
    navigateTo(url);
  }
};

export const isExistingSuggestion = (data, suggestions) => {
  return (
    isEqual(
      suggestions?.equipments,
      data?.equipments || data?.matchedEquipment || data?.equipment
    ) &&
    isEqual(
      suggestions?.otherEquipments,
      data?.otherEquipments || data?.partiallyMatchedEquipment
    )
  );
};

export const checkIsAlpPage = (params, path: string) => {
  const { partNumber, partName } = params;
  const esitURL = getSeoUrl(params, HEADLESS_ALP_PAGE);
  const alpURL = `${esitURL}/${partName}/${partNumber}`;
  const alp = path === alpURL;
  const subpage = partNumber && partName;
  return { isAlp: alp, isAlpSubpage: subpage };
};

export const handleSuggestionResults = (data) => {
  let suggestions = null;
  if ('equipments' in data) {
    suggestions = {
      equipments: data.equipments ?? [],
      otherEquipments: data.otherEquipments ?? []
    };
  } else if ('matchedEquipment' in data) {
    suggestions = {
      equipments: data.matchedEquipment ?? [],
      otherEquipments: data.partiallyMatchedEquipment ?? []
    };
  } else if ('equipment' in data) {
    suggestions = {
      equipments: data.equipment ?? []
    };
  }
  return suggestions;
};

export const getSerialNumberQuery = (
  serialNumber: string | undefined
): string =>
  serialNumber ? `?serialNumber=${serialNumber?.toUpperCase()}` : '';

export const getFormattedSeoUrl = (
  seoUrl: string,
  params: esiteParams | Record<string, string | string[]> | null
) => {
  const { esite, lang } = params;
  return seoUrl?.replace(`/${lang}/${esite}`, '');
};

export const getEquipmentUrl = (
  params: esiteParams,
  seoUrl: string,
  equipmentDetails?: {
    id: string;
    serialNumber: string;
  },
  subPath = ''
) => {
  const formattedSeoUrl = getFormattedSeoUrl(seoUrl, params);
  const serialNumberQuery = getSerialNumberQuery(
    equipmentDetails?.serialNumber
  );
  const alpUrl = `${ALP_URL}${formattedSeoUrl}${subPath}${serialNumberQuery}`;
  return getSeoUrl(params, alpUrl);
};

export const redirectToNewAlpUrl = async (
  router,
  featureFlag: boolean,
  isAlpEnableSoftNavigation: boolean,
  params: esiteParams | Record<string, string | string[]> | null,
  seoUrl: string,
  isSelectedEquipment = true,
  equipmentDetails?: {
    id: string;
    serialNumber: string;
  }
) => {
  const response =
    !isSelectedEquipment &&
    (await selectEquipment(
      equipmentDetails?.id,
      equipmentDetails?.serialNumber,
      false
    ));
  const formattedSeoUrl = getFormattedSeoUrl(seoUrl, params);
  const serialNumberQuery = getSerialNumberQuery(
    equipmentDetails?.serialNumber
  );
  const alpUrl = featureFlag
    ? `${ALP_URL}${formattedSeoUrl}${serialNumberQuery}`
    : formattedSeoUrl;
  const url = getSeoUrl(params, alpUrl);
  if (isSelectedEquipment || response.ok) {
    if (featureFlag) {
      navigateToALPPage(url, isAlpEnableSoftNavigation, router);
    } else {
      navigateTo(url);
    }
  } else {
    navigateToALPPage(url, isAlpEnableSoftNavigation, router);
  }
};

export const redirectToShopPartsSubpage = (
  seoUrl: string,
  params: esiteParams | Record<string, string | string[]> | null,
  isAssetLandingFeatureFlag: boolean,
  serialNumber: string
) => {
  const serialNumberQuery = getSerialNumberQuery(serialNumber);
  const formattedSeoUrl = getFormattedSeoUrl(seoUrl, params);
  const alpUrl = isAssetLandingFeatureFlag
    ? `${ALP_URL}${formattedSeoUrl}${SHOP_PARTS_PATH}${serialNumberQuery}`
    : `${formattedSeoUrl}#shopParts`;
  const url = getSeoUrl(params, alpUrl);
  return url;
};

export const getAlpSeoUrl = (seoUrl, pageUrl, params, serialNumber) => {
  const formattedUrl = getFormattedSeoUrl(seoUrl, params);
  const serialNumberQuery = getSerialNumberQuery(serialNumber);
  const pathMap = {
    [PARTS_DIAGRAM_PATH]: pageUrl.includes(PARTS_DIAGRAM_PATH),
    [MAINTENANCE_REPAIRS_PATH]: pageUrl.includes(MAINTENANCE_REPAIRS_PATH),
    [MANUALS_AND_RESOURCES_PATH]: pageUrl.includes(MANUALS_AND_RESOURCES_PATH),
    [SHOP_PARTS_PATH]: pageUrl.includes(SHOP_PARTS_PATH),
    [SERVICE_RECOMMENDATION_LIST_PAGE]: pageUrl.includes(
      SERVICE_RECOMMENDATION_LIST_PAGE
    )
  };
  const matchingPath = Object.keys(pathMap).find((key) => pathMap[key]) || '';
  return getSeoUrl(
    params,
    `${ALP_URL}${formattedUrl}${matchingPath}${serialNumberQuery}`
  );
};

export const getCategoryOptions = (repairOptions, defaultCategory = []) => {
  if (!repairOptions.length) return [];
  const uniqueCategories = new Set();
  return [
    ...defaultCategory,
    ...repairOptions
      .map(({ category }) => {
        const categoryName = capitalizeText(category);
        return uniqueCategories.has(categoryName)
          ? null
          : (uniqueCategories.add(categoryName),
            { label: categoryName, value: categoryName });
      })
      .filter(Boolean)
  ];
};

export const getAssetServiceInsights = (
  serviceInsightsDetails: ServiceInsightDetailProps,
  equipmentDetails: Equipment
) => {
  const activeServiceInsight =
    serviceInsightsDetails?.instanceSearchResponse?.filter(
      (instance: InstanceData) =>
        instance.state === SERVICE_ACTIVE &&
        instance.userRelatedState !== SERVICE_DISMISSED
    );
  if (!activeServiceInsight.length) return [];
  return activeServiceInsight?.filter(
    (instance: InstanceData) => instance.assetId === equipmentDetails.assetId
  );
};
